import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/ordermodal.css';
import { useNavigate } from 'react-router-dom';

function OrderModal({ isOpen, onClose, type, initialBalance = 0.00 }) {
    const [selectedDuration, setSelectedDuration] = useState('60S');
    const [amount, setAmount] = useState(0);
    const [error, setError] = useState(null);
    const [isTrading, setIsTrading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [tradeResult, setTradeResult] = useState(null);
    const tradingPair = "BTCUSD";
    const [balance, setBalance] = useState(initialBalance);
    const [userData, setUserData] = useState(null);
    const [isLoadingBalance, setIsLoadingBalance] = useState(true);
    const [price, setPrice] = useState(null); // Real-time price when trade starts
    const [lastPrice, setLastPrice] = useState(null); // Real-time lastPrice when countdown = 29
    const [dataSent, setDataSent] = useState(false); // Flag to ensure data is sent only once
    const hasSentRef = useRef(false); // Ref to track API call outside state

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const handleDurationSelect = (duration) => {
        setSelectedDuration(duration);
    };

    const handleAmountChange = (e) => {
        setAmount(parseFloat(e.target.value) || 0);
        setError(null);
    };

    const calculateProfits = () => {
        const profitPercent = {
            '60S': 0.12,
            '120S': 0.18,
            '180S': 0.25,
        }[selectedDuration];
        return (amount * profitPercent).toFixed(2) || '0.00';
    };

    const validateAmount = () => {
        if (amount < 100) {
            setError('Minimum 100$');
            return false;
        }
        if (amount > balance) {
            setError('Insufficient balance');
            return false;
        }
        setError(null);
        return true;
    };

    const fetchUserData = useCallback(() => {
        try {
            const storedUser = localStorage.getItem("user");
            console.log("Raw stored user:", storedUser);
            if (storedUser) {
                const user = JSON.parse(storedUser);
                console.log("Parsed user:", user);
                if (!user.id) {
                    console.warn("id missing in parsed data, using fallback.");
                    user.id = "ec2f3fac-dfe5-4470-97fb-53cd9b605490";
                }
                setUserData(user);
            } else {
                setError("No user data found in localStorage.");
                console.warn("No user data, setting fallback user.");
                setUserData({ id: "ec2f3fac-dfe5-4470-97fb-53cd9b605490", username: "Engsong" });
                setIsLoadingBalance(false);
            }
        } catch (error) {
            console.error("Error parsing user data:", error);
            setError("Failed to retrieve user data, using fallback.");
            setUserData({ id: "ec2f3fac-dfe5-4470-97fb-53cd9b605490", username: "Engsong" });
            setIsLoadingBalance(false);
        }
    }, []);

    const fetchBalance = useCallback(async () => {
        if (!userData?.username) {
            setError("Username is not available.");
            setIsLoadingBalance(false);
            return;
        }

        setIsLoadingBalance(true);
        const encodedUsername = encodeURIComponent(userData.username);
        const url = `${baseUrl}/wallet/username/${encodedUsername}`;

        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const data = await response.json();
            setBalance(parseFloat(data.amount) || initialBalance);
            setError(null);
        } catch (err) {
            console.error("Error fetching balance:", err);
            setError(err.message || "Failed to fetch balance");
            setBalance(initialBalance);
        } finally {
            setIsLoadingBalance(false);
        }
    }, [userData, initialBalance]);

    const fetchMarketData = useCallback(async () => {
        try {
            const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=BTCUSDT');
            if (!response.ok) {
                throw new Error('Failed to fetch market data');
            }
            const data = await response.json();
            const currentPrice = parseFloat(data.price);
            return currentPrice;
        } catch (err) {
            console.error("Error fetching market data:", err);
            return 50000; // Fallback price
        }
    }, []);

    const fetchTradeStatus = useCallback(async () => {
        if (!userData?.id) {
            setError("User ID is not available for status fetch.");
            console.error("Missing id in userData:", userData);
            return null;
        }

        const url = `${baseUrl}/users/${userData.id}`;
        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const data = await response.json();
            console.log("Trade status fetched:", data);
            return data.status;
        } catch (err) {
            console.error("Error fetching trade status:", err);
            setError(err.message || "Failed to fetch trade status");
            return null;
        }
    }, [userData]);

    const postTradeData = useCallback(async (currentPrice, currentLastPrice) => {
        if (!userData?.id) {
            setError("User ID is not available.");
            console.error("Missing id in userData:", userData);
            return false;
        }

        const payload = {
            userId: userData.id,
            symbol: `X:${tradingPair}`,
            investment: amount,
            profitPercentage: getProfitPercentage(),
            position: type,
            price: currentPrice,
            lastPrice: currentLastPrice,
        };

        console.log("Posting trade data:", payload);

        try {
            
            const response = await fetch(`${baseUrl}/orders`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const data = await response.json();
            console.log("Trade response:", data);
            return true;
        } catch (err) {
            console.error("Error posting trade data:", err);
            setError(err.message || "Failed to create trade");
            return false;
        }
    }, [userData, amount, type]);

    const getProfitPercentage = () => {
        return {
            '60S': 12,
            '120S': 18,
            '180S': 25,
        }[selectedDuration] || 0;
    };

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        if (userData?.username) {
            fetchBalance();
        }
    }, [userData, fetchBalance]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleConfirm = async () => {
        if (validateAmount()) {
            console.log("Validation passed, fetching initial price...");
            const realTimePrice = await fetchMarketData();
            setPrice(realTimePrice);
            console.log("Real-time price at confirm:", realTimePrice);

            onClose();
            setIsTrading(true);
            setTradeResult(null);
            setDataSent(false);
            hasSentRef.current = false; // Reset ref
            const durationSeconds = parseInt(selectedDuration.replace('S', ''));
            setCountdown(durationSeconds);
            setBalance(prev => prev - amount);
        } else {
            console.log("Validation failed:", error);
        }
    };

    useEffect(() => {
        let timer;
        if (isTrading && countdown > 0) {
            console.log("Countdown running:", countdown);
            timer = setInterval(async () => {
                setCountdown((prev) => {
                    const newCountdown = prev - 1;
                    console.log("Decreasing countdown to:", newCountdown);
                    return newCountdown;
                });

                // Check countdown outside setCountdown to avoid batching issues
                if (countdown === 29 && !hasSentRef.current) {
                    console.log("Triggering API call at countdown = 29");
                    const realTimeLastPrice = await fetchMarketData();
                    setLastPrice(realTimeLastPrice);
                    console.log("Real-time lastPrice at countdown = 29:", realTimeLastPrice);
                    const success = await postTradeData(price, realTimeLastPrice);
                    if (success) {
                        hasSentRef.current = true; // Use ref to ensure single execution
                        setDataSent(true); // Update state for rendering
                    }
                }
            }, 1000);
        } else if (isTrading && countdown === 0 && tradeResult === null) {
            const determineTradeResult = async () => {
                const status = await fetchTradeStatus();
                if (status === 0) {
                    setTradeResult('win');
                    setBalance(prev => prev + parseFloat(calculateProfits()));
                    console.log("Trade completed, result: win");
                } else if (status === 1) {
                    setTradeResult('loss');
                    console.log("Trade completed, result: loss");
                } else {
                    const isWin = Math.random() > 0.5;
                    setTradeResult(isWin ? 'win' : 'loss');
                    if (isWin) {
                        setBalance(prev => prev + parseFloat(calculateProfits()));
                    }
                    console.log("Trade status fetch failed, using fallback result:", isWin ? 'win' : 'loss');
                }
            };
            determineTradeResult();
        }
        return () => {
            if (timer) {
                console.log("Cleaning up timer.");
                clearInterval(timer);
            }
        };
    }, [isTrading, countdown, tradeResult, fetchTradeStatus, price]);

    const handleFinish = () => {
        navigate('/trade');
        setIsTrading(false);
        setTradeResult(null);
        setPrice(null);
        setLastPrice(null);
        setDataSent(false);
        hasSentRef.current = false; // Reset ref
        
    };

    if (!isOpen && !isTrading) return null;

    return (
        <>
            {isOpen && (
                <div className="modal-overlay" onClick={onClose}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="order-info">
                            <h3>Order Information</h3>
                            <div className="order-details">
                                <div className="value trading-pair">Trading pair <span className="trading-pair">{tradingPair}</span></div>
                                <div className="value trading-pair">Direction<span className="direction" style={{ color: type === 'sell' ? '#d32f2f' : '' }}>
                                    {type === 'buy' ? 'BUY' : 'SELL'}
                                </span></div>
                                <div className="direction-options">
                                    <button
                                        className={`direction-button ${selectedDuration === '60S' ? 'selected' : ''}`}
                                        onClick={() => handleDurationSelect('60S')}
                                    >
                                        60S <span className="profit">+12%</span>
                                    </button>
                                    <button
                                        className={`direction-button ${selectedDuration === '120S' ? 'selected' : ''}`}
                                        onClick={() => handleDurationSelect('120S')}
                                    >
                                        120S <span className="profit">+18%</span>
                                    </button>
                                    <button
                                        className={`direction-button ${selectedDuration === '180S' ? 'selected' : ''}`}
                                        onClick={() => handleDurationSelect('180S')}
                                    >
                                        180S <span className="profit">+25%</span>
                                    </button>
                                </div>
                                <input
                                    type="number"
                                    className="amount-input"
                                    placeholder="amount"
                                    value={amount}
                                    onChange={handleAmountChange}
                                />
                                <div className="profits-row">
                                    <div className="value profits">
                                        <span className="detail-label">Profits</span>
                                        <span>${calculateProfits()}</span></div>
                                </div>
                                <div className="balance-row">
                                    <div className="value balance">
                                        <span className="detail-label">Balance</span>
                                        <span>{isLoadingBalance ? 'Loading...' : `$${balance.toFixed(2)}`}</span>
                                    </div>
                                </div>

                                {error && (
                                    <div className="error-notification" onClick={() => setError(null)}>
                                        <span className="error-icon">!</span> {error}
                                    </div>
                                )}

                                <button className="confirm-button" style={{ backgroundColor: type === 'sell' ? '#d32f2f' : '', color: 'white' }} onClick={handleConfirm}>
                                    {type === 'buy' ? 'CONFIRMED BUY' : 'CONFIRMED SELL'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isTrading && (
                <div className="trade-modal-overlay">
                    <div className="trade-modal-content">
                        <h3>{countdown > 0 ? 'Please wait while making a trade' : `Trade Result: ${tradeResult === 'win' ? 'Win' : 'Loss'}`}</h3>
                        <div className="progress-circle" style={{ '--progress': `${(countdown / parseInt(selectedDuration.replace('S', ''))) * 100}%` }}>
                            <span className={`progress-value ${countdown === 0 && tradeResult === 'loss' ? 'loss-text' : ''}`}>
                                {countdown > 0 ? countdown : (tradeResult === 'win' ? `+${calculateProfits()}` : `-${amount.toFixed(2)}`)}
                            </span>
                        </div>
                        <div className="trade-details">
                            <p>
                                <span className="detail-label">Symbol:</span>
                                <span className="detail-value">X{tradingPair}</span>
                            </p>
                            <p>
                                <span className="detail-label">Investment:</span>
                                <span className="detail-value">${amount.toFixed(2)}</span>
                            </p>
                            <p>
                                <span className="detail-label">Profit:</span>
                                <span className="detail-value">{tradeResult === 'win' ? `+${calculateProfits()}` : '0.00'}</span>
                            </p>
                            <p>
                                <span className="detail-label">Position:</span>
                                <span className="detail-value">
                                    (<span style={{ color: type === 'sell' ? '#d32f2f' : '#28a745' }}>
                                        {type === 'buy' ? 'Buy' : 'Sell'}
                                    </span>)
                                </span>
                            </p>
                        </div>
                        <button
                            className="finished-button"
                            onClick={handleFinish}
                            disabled={countdown > 0}
                        >
                            FINISHED
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default OrderModal;